import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { fetchCheckoutStatus } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatINRFromMinor } from "@/lib/format";

export default function CheckoutSuccess() {
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [status, setStatus] = useState({ state: "pending", data: null, attempt: 0 });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", data: null, attempt: 0 });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await fetchCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data, attempt: attempts });
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired", data, attempt: attempts });
          return;
        }
        setStatus({ state: "pending", data, attempt: attempts });
        if (attempts >= maxAttempts) {
          setStatus({ state: "timeout", data, attempt: attempts });
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= maxAttempts) {
          setStatus({ state: "error", data: null, attempt: attempts });
          return;
        }
        setTimeout(tick, 2000);
      }
    };

    tick();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div data-testid="checkout-success" className="pt-32 md:pt-40 min-h-[80vh] px-6 md:px-12 lg:px-16">
      <div className="max-w-3xl">
        <p className="overline mb-8 text-stone">Order N°{sessionId ? sessionId.slice(-8).toUpperCase() : "—"}</p>

        {status.state === "pending" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Loader2 className="animate-spin text-moss" size={20} />
              <p className="overline text-charcoal">Confirming with Stripe…</p>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.95] tracking-tight text-ink">
              A moment, <em className="italic text-moss">please.</em>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-charcoal font-light max-w-xl">
              We are listening for confirmation from the payment processor.
              This usually takes seconds. Do not refresh this page.
            </p>
          </>
        )}

        {status.state === "paid" && (
          <>
            <div className="flex items-center gap-3 mb-6 text-moss">
              <Check size={20} />
              <p className="overline">Held in good order</p>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.95] tracking-tight text-ink">
              Thank you,<br />
              <em className="italic text-moss">it is held.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-charcoal font-light max-w-xl leading-relaxed">
              Your objects will be packed by hand and dispatched within the
              week. A confirmation has been recorded against your session.
            </p>

            {status.data?.summary && (
              <div className="mt-12 border-t border-hairline pt-6 max-w-xl">
                <p className="overline mb-3">In your dispatch</p>
                <p className="font-display italic text-xl text-ink">{status.data.summary}</p>
                <p className="font-display text-3xl text-ink mt-6">
                  {formatINRFromMinor(status.data.amount_total)}
                  <span className="text-stone text-base ml-2 font-body uppercase tracking-widest">{(status.data.currency || 'inr').toUpperCase()}</span>
                </p>
              </div>
            )}

            <div className="mt-12 flex flex-wrap gap-6">
              <Link to="/journal" className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink px-6 py-4 hover:bg-moss transition-colors" data-testid="success-cta-journal">
                Return to the Journal <ArrowUpRight size={14} />
              </Link>
              <Link to="/shop" className="text-[0.72rem] tracking-[0.22em] uppercase font-medium text-ink link-underline self-center">
                Browse more objects
              </Link>
            </div>
          </>
        )}

        {(status.state === "timeout" || status.state === "expired" || status.state === "error") && (
          <>
            <p className="overline mb-6 text-stone">{status.state === "expired" ? "Session expired" : "Status unclear"}</p>
            <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.95] tracking-tight text-ink">
              We are not <em className="italic">certain yet.</em>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-charcoal font-light max-w-xl">
              If the payment cleared, a confirmation will arrive in your inbox.
              Otherwise, no charge has been made and you may begin again.
            </p>
            <div className="mt-10 flex flex-wrap gap-6">
              <Link to="/shop" className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink px-6 py-4 hover:bg-moss transition-colors">
                Return to the Shop <ArrowUpRight size={14} />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
